/**
 * 报名 API（enrollment-submission-source-alignment:SC-53~SC-58 前端契约）。
 *
 * 对齐后端 enrollment-api 模块 REST 契约：
 * - POST   /enrollments                        -> SubmitEnrollmentResult（组结构 submission+applications）
 * - GET    /enrollments                        -> Paginated<EnrollmentItem>（单条口径 + 归属组字段）
 * - GET    /enrollments/:id                    -> EnrollmentItem（单条兼容 + 归属字段）
 * - PATCH  /enrollments/:id                    -> { submissionId }（代理到组编辑整组生效）
 * - GET    /enrollments/:id/convert-candidates -> SubmissionConvertCandidatesResult（组级）
 * - GET    /enrollment-submissions             -> Paginated<EnrollmentSubmissionItem>（组级）
 * - GET    /enrollment-submissions/:id         -> EnrollmentSubmissionDetail（双层）
 * - PATCH  /enrollment-submissions/:id         -> { submissionId }（组编辑）
 * - GET    /enrollment-submissions/:id/convert-candidates -> SubmissionConvertCandidatesResult
 *
 * 路径从 api.config.ts 导入，禁止内联；Transport 统一解包 `{success,data}` 信封。
 */
import { ApiPaths } from './api.config';
import { http } from './transport';
import type { Paginated } from './campuses.api';

/** 转化状态五态（服务端派生，对齐 derive 算法）。 */
export type EnrollmentConversionStatus =
  | 'PENDING_TRIAL'
  | 'IN_TRIAL'
  | 'TRIAL_ENDED_FOLLOW_UP'
  | 'CONVERTED_PENDING_PAYMENT'
  | 'CONVERTED_PAID';

/** 转化状态中文标签。 */
export const CONVERSION_STATUS_LABEL: Record<EnrollmentConversionStatus, string> = {
  PENDING_TRIAL: '待试课',
  IN_TRIAL: '试课中',
  TRIAL_ENDED_FOLLOW_UP: '试课结束待跟进',
  CONVERTED_PENDING_PAYMENT: '已转订单待支付',
  CONVERTED_PAID: '已成交',
};

/** 报名列表项/详情（单条口径 + 归属提交组/来源商品，SC-30/SC-38）。 */
export interface EnrollmentItem {
  applicationId: string;
  studentId: string;
  studentName: string;
  skuId: string;
  skuName: string;
  campusId: string;
  campusName: string;
  /** 归属报名提交组。 */
  submissionId: string;
  /** 来源商品（无来源为 null）。 */
  sourceProductId: string | null;
  source: string;
  consultantId: string | null;
  consultantName: string | null;
  trialGranted: number;
  /** 赠送课剩余（报名试课与手工赠课归赠送课池）。 */
  giftedBalance: number;
  formalBalance: number;
  status: string;
  /** 关联订单号（预留，4-1 前恒空）。 */
  orderNo: string | null;
  note: string | null;
  submittedAt: string;
  conversionStatus: EnrollmentConversionStatus;
}

/** 单条子报名提交结果（组内逐条）。 */
export interface EnrollmentApplicationResultItem {
  applicationId: string;
  skuId: string;
  status: string;
  trialGranted: number;
  /** 赠送课池余额（同事务赠课后）。 */
  giftedBalance: number;
}

/** 提交报名结果（submission + applications 组结构，SC-13）。 */
export interface SubmitEnrollmentResult {
  submissionId: string;
  sourceProductId: string | null;
  replayed: boolean;
  applications: EnrollmentApplicationResultItem[];
}

/** 提交组级列表行（组级关键字段 + n/m 已购派生，SC-11）。 */
export interface EnrollmentSubmissionItem {
  submissionId: string;
  studentId: string;
  studentName: string;
  campusId: string;
  campusName: string;
  sourceProductId: string | null;
  sourceProductName: string | null;
  source: string;
  consultantId: string | null;
  consultantName: string | null;
  note: string | null;
  submittedAt: string;
  applicationCount: number;
  convertedCount: number;
  /** 组级转化进度展示（如 `0/2 已购`）。 */
  conversionProgress: string;
}

/** 来源商品快照摘要（入口解释）。 */
export interface SourceProductSnapshot {
  productId: string;
  name: string;
  status: string;
  price: number;
  depositRatio: number;
  finalDueDays: number;
  /** SKU 清单（含提交时刻生效试课次数：商品覆盖优先，存量快照无该字段）。 */
  skus: Array<{
    skuId: string;
    skuName: string;
    lessonCount: number;
    trialSessionCount?: number;
  }>;
}

/** 提交组级详情（双层：组 + 组内子报名，SC-12）。 */
export interface EnrollmentSubmissionDetail extends EnrollmentSubmissionItem {
  sourceProductSnapshot: SourceProductSnapshot | null;
  applications: EnrollmentItem[];
}

/** 组级候选商品（三类差异，§16.2.8 冻结字段集）。 */
export interface SubmissionCandidate {
  productId: string;
  productName: string;
  price: number;
  /** 首付款比例（服务端候选随商品引用一并返回）。 */
  depositRatio: number;
  /** 尾款周期（天）。 */
  finalDueDays: number;
  coveredSkuIds: string[];
  uncoveredSkuIds: string[];
  newSkuIds: string[];
}

/** 组级转订单候选响应（SC-43~SC-46）。 */
export interface SubmissionConvertCandidatesResult {
  submissionId: string;
  studentId: string;
  studentName: string;
  campusId: string;
  sourceProductId: string | null;
  defaultProductId: string | null;
  convertibility: 'CONVERTIBLE' | 'NO_CANDIDATE';
  applications: Array<{
    applicationId: string;
    skuId: string;
    skuName: string;
    covered: boolean;
    /** 赠送课剩余（单行双池读）。 */
    giftedBalance: number;
  }>;
  candidates: SubmissionCandidate[];
}

/** 报名列表查询参数（单条口径）。 */
export interface EnrollmentListQuery {
  campusId?: string;
  skuId?: string;
  source?: string;
  conversionStatus?: EnrollmentConversionStatus;
  dateFrom?: string;
  dateTo?: string;
  keyword?: string;
  studentId?: string;
  page?: number;
  pageSize?: number;
}

/** 提交组列表查询参数（组级，SC-11 三维度）。 */
export interface EnrollmentSubmissionListQuery {
  campusId?: string;
  sourceProductId?: string;
  keyword?: string;
  page?: number;
  pageSize?: number;
}

/** 提交报名载荷（管理端代建；学生引用二选一；来源商品可空）。 */
export interface SubmitEnrollmentPayload {
  studentId?: string;
  newStudent?: {
    name: string;
    gender: string;
    gradeStage: string;
    homeCampusId?: string;
    guardianMobile: string;
    guardians: Array<{ name: string; relationship: string }>;
    subjectIds?: string[];
    tags?: string[];
  };
  skuIds: string[];
  campusId: string;
  /** 来源商品（入口商品非成交商品；直报/代建可空）。 */
  sourceProductId?: string | null;
  source: string;
  consultantId?: string | null;
  /** 科目分数（key = subjectId，仅含已填科目；null = 不录入） */
  scores?: Record<string, number> | null;
  note?: string | null;
  idempotencyKey: string;
}

/** 组级编辑安全字段载荷（一次作用整组；SKU 与校区不可改）。 */
export interface UpdateEnrollmentSubmissionPayload {
  source?: string;
  consultantId?: string | null;
  /** 科目分数（key = subjectId，仅含已填科目；null = 清空该组涉及科目分数） */
  scores?: Record<string, number> | null;
  note?: string | null;
}

/** 构造列表查询字符串（undefined 字段不拼入）。 */
function buildListQuery(query: EnrollmentListQuery): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query as Record<string, unknown>)) {
    if (value !== undefined && value !== null && `${value}` !== '') {
      params.set(key, `${value}`);
    }
  }
  const qs = params.toString();
  const base = ApiPaths.enrollments.list;
  return qs ? `${base}?${qs}` : base;
}

function buildSubmissionListQuery(query: EnrollmentSubmissionListQuery): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && `${value}` !== '') {
      params.set(key, `${value}`);
    }
  }
  const qs = params.toString();
  const base = ApiPaths.enrollmentSubmissions.list;
  return qs ? `${base}?${qs}` : base;
}

async function listEnrollments(
  query: EnrollmentListQuery = {},
): Promise<Paginated<EnrollmentItem>> {
  return http.get<Paginated<EnrollmentItem>>(buildListQuery(query));
}

async function getEnrollment(id: string): Promise<EnrollmentItem> {
  return http.get<EnrollmentItem>(ApiPaths.enrollments.detail(id));
}

async function submitEnrollment(
  payload: SubmitEnrollmentPayload,
): Promise<SubmitEnrollmentResult> {
  return http.post<SubmitEnrollmentResult>(
    ApiPaths.enrollments.create,
    payload,
  );
}

/** 单报名编辑（兼容路径：代理到组编辑，整组生效，响应 {submissionId}）。 */
async function updateEnrollment(
  id: string,
  payload: UpdateEnrollmentSubmissionPayload,
): Promise<{ submissionId: string }> {
  return http.patch<{ submissionId: string }>(
    ApiPaths.enrollments.update(id),
    payload,
  );
}

/** 单报名候选（兼容路径：返回组级三类差异结果）。 */
async function listConvertCandidates(
  id: string,
): Promise<SubmissionConvertCandidatesResult> {
  return http.get<SubmissionConvertCandidatesResult>(
    ApiPaths.enrollments.convertCandidates(id),
  );
}

/** 组级列表（组级聚合 + 来源商品维度筛选 + 组进度派生）。 */
async function listEnrollmentSubmissions(
  query: EnrollmentSubmissionListQuery = {},
): Promise<Paginated<EnrollmentSubmissionItem>> {
  return http.get<Paginated<EnrollmentSubmissionItem>>(
    buildSubmissionListQuery(query),
  );
}

/** 组级详情（双层：组 + 组内子报名）。 */
async function getEnrollmentSubmission(
  id: string,
): Promise<EnrollmentSubmissionDetail> {
  return http.get<EnrollmentSubmissionDetail>(
    ApiPaths.enrollmentSubmissions.detail(id),
  );
}

/** 组级编辑（一次作用整组）。 */
async function updateEnrollmentSubmission(
  id: string,
  payload: UpdateEnrollmentSubmissionPayload,
): Promise<{ submissionId: string }> {
  return http.patch<{ submissionId: string }>(
    ApiPaths.enrollmentSubmissions.update(id),
    payload,
  );
}

/** 组级转订单候选（三类差异 + 默认选中来源商品）。 */
async function listSubmissionConvertCandidates(
  id: string,
): Promise<SubmissionConvertCandidatesResult> {
  return http.get<SubmissionConvertCandidatesResult>(
    ApiPaths.enrollmentSubmissions.convertCandidates(id),
  );
}

export const enrollmentsApi = {
  listEnrollments,
  getEnrollment,
  submitEnrollment,
  updateEnrollment,
  listConvertCandidates,
  listEnrollmentSubmissions,
  getEnrollmentSubmission,
  updateEnrollmentSubmission,
  listSubmissionConvertCandidates,
};
